<template>
  <div class="constrain-width || mobile">
    <PageHeader
      :headerArch="pageType === pageTypes.partnerPaymentPlan && !error"
    />
    <Transition>
      <PagePaymentError v-if="error" :errorMessage="error" />
      <PagePartnerSuccess
        v-else-if="pageType === pageTypes.partnerPaymentSuccess"
        :content="pageData"
        :action="() => setCurrentPage(defaultProgression)" />
      <PagePartnerPaymentDownload
        v-else-if="pageType === pageTypes.partnerPaymentDownload"
        :title="pageData.title"
        :formData="partnerForm" />
      <PagePartnerPayment
        v-else-if="pageType === pageTypes.partnerPaymentPlan"
        :updateFormState="updateFormState"
        :submitFormData="submitPaymentProcess"
        :formData="partnerForm"
        :isLoading="isLoading"
        :warning="warning"
    /></Transition>
  </div>
</template>
<script>
import PageHeader from "@/components/PageHeader.vue";
import PagePartnerPayment from "./Payment/PagePartnerPayment.vue";
import PagePaymentError from "./Error/PagePartnerError.vue";
import PagePartnerSuccess from "./Success/PagePartnerPaymentSuccess.vue";
import PagePartnerPaymentDownload from "./Success/PagePartnerPaymentDownload.vue";
import {
  partnerFormState,
  partnerPaymentFields,
  PAYMENT_FORMS,
  MODULES,
  PAGE_TYPES,
} from "@/config";
import {
  createFormState,
  denormalisePartnerPaymentForm,
  denormaliseOrderSummary,
} from "@/utils";
import * as queryString from "query-string";
import { pageInformationMixin } from "@/mixins/index.js";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      error: null,
      warning: null,
      isLoading: false,
      pageTypes: PAGE_TYPES,
      whiteListedQueryParams: ["plan_id"],
      [PAYMENT_FORMS.partner]: createFormState(
        partnerPaymentFields,
        partnerFormState
      ),
    };
  },
  components: {
    PageHeader,
    PagePartnerPayment,
    PagePartnerSuccess,
    PagePaymentError,
    PagePartnerPaymentDownload,
  },
  mixins: [pageInformationMixin],
  computed: {
    ...mapGetters(MODULES.APP, {
      referrer: "getReferrer",
    }),
  },
  mounted() {
    const { dispatch } = this.$store;

    dispatch(`${MODULES.COUNTRY}/getCountriesAction`);

    dispatch(`${MODULES.PLAN}/getAllPlansAction`).then((plans) => {
      this.setInitialSearchParams((data) => {
        const planData = plans.find((plan) => plan.id === Number(data.plan_id));

        if (!planData) {
          this.setError(
            "Oops, something went wrong with your link! It appears this plan does not exist. Please contact support@ovrcome.io"
          );
        }

        this.updateFormState(
          PAYMENT_FORMS.partner,
          "category_id",
          planData.category_id,
          false
        );

        dispatch(`${MODULES.PAYMENT}/getSaleSummaryAction`, {
          item_id: data.plan_id,
          paymentType: "partner",
        });
      });
    });
  },
  methods: {
    setError(err) {
      this.error = err;
    },
    setWarning(warning) {
      this.warning = warning;
    },
    setCurrentPage(item) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.APP}/setCurrentPage`, item);
    },
    setInitialSearchParams(callback) {
      const search = location.hash.replace("#/", "");

      if (search) {
        const obj = queryString.parse(search);

        const check = (key) => !obj[key];

        if (this.whiteListedQueryParams.some(check)) {
          this.setError(
            `Oops, something went wrong with your link! You are missing: ${this.whiteListedQueryParams
              .filter((key) => !obj[key])
              .toString()}. Please contact support@ovrcome.io`
          );

          return;
        }

        Object.entries(obj)
          .filter((item) => this.whiteListedQueryParams.includes(item[0]))
          .forEach((entry) => {
            this.updateFormState(
              PAYMENT_FORMS.partner,
              entry[0],
              entry[1],
              false
            );
          });

        callback(obj);
      }
    },
    updateFormState(formName, field, value, touched) {
      this[formName][field] = { value, touched };

      if (field === "countryId") {
        const payload = {
          country_id: value,
        };

        this.getCountryDeliveryMethods(formName, payload);
      }
    },
    getCountryStates(id) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.COUNTRY}/getCountryStatesById`, id);
    },
    getCountryDeliveryMethods(formName, payload) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.COUNTRY}/getCountryDeliveryMethods`, payload).then(
        (data) => {
          if (data.length > 0) {
            const payload = {
              delivery_method_id: data[0].id,
            };

            this.getSaleSummary(formName, payload);
          }
        }
      );
    },
    getSaleSummary(formName, payload) {
      const { dispatch } = this.$store;
      const form = this[formName];

      let data = {
        ...denormaliseOrderSummary(form),
        ...payload,
        item_id: form.plan_id.value,
        item_type: form.itemType.value,
        paymentType: "partner",
        voucher_code: this.referrer.voucher.code,
      };

      dispatch(`${MODULES.PAYMENT}/getSaleSummaryAction`, data).then(() => {
        if (payload?.delivery_method_id) {
          this.updateFormState(
            formName,
            "deliveryMethod",
            payload.delivery_method_id,
            false
          );
        }
      });
    },
    chainWarning(err) {
      this.isLoading = false;
      this.setWarning(
        err?.error ||
          err?.message ||
          "Something went wrong. Please contact ovrcome support: support@ovrcome.io"
      );

      return Promise.reject(err);
    },
    submitPaymentProcess: async function () {
      const { chainWarning, referrer, setCurrentPage, defaultProgression } =
        this;
      const { dispatch } = this.$store;

      const form = this[PAYMENT_FORMS.partner];

      let denormalisedPayload = denormalisePartnerPaymentForm(form);

      Object.keys(denormalisedPayload).forEach((key) => {
        if (!denormalisedPayload[key]) {
          delete denormalisedPayload[key];
        }
      });

      this.isLoading = true;

      dispatch(`${MODULES.PAYMENT}/submitPayment`, {
        ...denormalisedPayload,
        referrer: referrer.user_source,
        voucher_code: referrer.voucher.code,
        paymentType: "partner",
      })
        .then(() => (this.isLoading = false))
        .then(() => setCurrentPage(defaultProgression))
        .catch(chainWarning);
    },
  },
};
</script>
